import { useMemo } from "react";
import { useBingoGameStore } from "@/plugins/bingo/state/bingoGameStore";

/**
 * BoardMirror: 90 cells arranged as a 10-column × 9-row grid.
 * Row r (1..9) shows the numbers 10*(r-1)+1 .. 10*r.
 *
 * - Called numbers glow with the primary color.
 * - Manual marks (host correction) also glow but with a subtle dot.
 * - The most recent called number pulses.
 * - Right-click (or context menu) toggles a manual mark for corrections.
 */
export default function BoardMirror() {
  const called = useBingoGameStore((s) => s.called);
  const manual = useBingoGameStore((s) => s.manualMarks);
  const toggleManualMark = useBingoGameStore((s) => s.toggleManualMark);

  const calledSet = useMemo(
    () => new Set(called.map((c) => c.value)),
    [called],
  );
  const manualSet = useMemo(() => new Set(manual), [manual]);
  const lastCalled = called.length ? called[called.length - 1].value : null;

  return (
    <div className="w-full h-full flex flex-col">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-text-muted)] mb-3">
        Board · 1 – 90
        <span className="ml-3 text-[color:var(--ic-text-muted)] normal-case tracking-normal">
          right-click a cell to mark/unmark manually
        </span>
      </div>
      <div
        data-testid="host-board-mirror"
        className="grid gap-1.5 flex-1 min-h-0"
        style={{
          gridTemplateColumns: "repeat(10, minmax(0, 1fr))",
          gridTemplateRows: "repeat(9, minmax(0, 1fr))",
        }}
      >
        {Array.from({ length: 90 }, (_, i) => {
          const v = i + 1;
          const isCalled = calledSet.has(v);
          const isManual = manualSet.has(v);
          const isLast = v === lastCalled;
          const marked = isCalled || isManual;

          return (
            <button
              key={v}
              type="button"
              data-testid={`host-cell-${v}`}
              onContextMenu={(e) => {
                e.preventDefault();
                toggleManualMark(v);
              }}
              className="ic-font-display rounded-md text-lg font-semibold flex items-center justify-center transition-all select-none relative"
              style={
                marked
                  ? {
                      background: "color-mix(in srgb, var(--ic-primary) 22%, transparent)",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: "color-mix(in srgb, var(--ic-primary) 55%, transparent)",
                      color: "#fff",
                      boxShadow: isLast
                        ? "0 0 24px var(--ic-primary-glow), inset 0 0 8px var(--ic-primary-glow)"
                        : "0 0 8px var(--ic-primary-glow)",
                      transform: isLast ? "scale(1.04)" : "scale(1)",
                    }
                  : {
                      background: "transparent",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderColor: "var(--ic-border-subtle)",
                      color: "var(--ic-text-muted)",
                    }
              }
            >
              {v}
              {isManual && !isCalled && (
                <span
                  className="absolute top-0.5 right-1 text-[8px] tracking-widest opacity-70"
                  aria-label="manually marked"
                >
                  •
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
